import React, { useState, useEffect, useCallback } from "react";
import debounce from "lodash.debounce";
import { useDispatch } from "react-redux";
import InputField from "common/customInputField";
import SelectInput from "common/selectInput";
import Button from "react-bootstrap/Button";
import Modal from "common/modal";
import DataTable from "components/common/desktopDataTable";
import { displayDesktopRowItem } from "../../common/desktopDataTable/utils";
import AutoComplete from "common/autoComplete";
import warning from "svg/warning.svg";
import { storeLookupHeaders } from "utils/common";
import { showSpinner, hideSpinner } from "store/slices/spinner";
import { api } from "store/slices/api";
import "./style.scss";

const StoreLookup = ({
  numberFieldChangeCallback,
  nameFieldChangeCallback,
  mobileView,
  onStoreLookupValidation,
  debounceValue,
  defaultData,
  nameFieldWrapperClass,
  numberFieldWrapperClass,
  isEdit,
  createQuoteFlag,
  requiredFields,
}) => {
  const dispatch = useDispatch();
  const { data: statesList } = api.useGetStatesQuery();
  const [state, setState] = useState({
    storeNumber: "",
    lookupStoreNumber: "",
    lookupStoreName: "",
    lookupStoreAddress: "",
    lookupStoreState: "",
    lookupStoreCity: "",
    lookupStoreZip: "",
    lookupResults: [],
    isShowLookupTable: false,
  });
  const [isShowModal, setIsShowModal] = useState(false);
  const [storeNumberError, setStoreNumberError] = useState("");
  const [storeNameError, setStoreNameError] = useState("");
  const [storeData, setStoreData] = useState({});
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [storeName, setStoreName] = useState("");
  const [isNameSelected, setIsNameSelected] = useState(true);
  const [isDefaultDataSet, setIsDefaultDataSet] = useState(false);
  const [isOnChangeUsed, setIsOnChangeUsed] = useState(false);
  const tdClass = "table-root__body__tr__td table-root__body__tr__td__text";

  const [fetchStoreInfo] = api.useLazyGetStoreInfoQuery();

  useEffect(() => {
    if (defaultData && Object.keys(defaultData).length > 0) {
      setState({ ...state, storeNumber: defaultData.storeNumber });
      setStoreName(defaultData.storeName);
      setIsDefaultDataSet(true);

      if (createQuoteFlag && defaultData.storeNumber) {
        fetchStoreInfo({ storeNumber: defaultData.storeNumber }).then((res) => {
          if (res.data.length) {
            setState({ ...state, ...res.data[0] });
            setStoreName(res.data[0].storeName);
            setStoreData(res.data[0]);
          }
          numberFieldChangeCallback(defaultData.storeNumber, res.data[0]);
        });
      }
    } else {
      setIsDefaultDataSet(true);
    }
  }, [defaultData]);

  useEffect(() => {
    if (state.storeNumber?.length === 0) {
      setStoreNumberError("Please enter Store Number");
    } else if (state.storeNumber?.length && state.storeNumber?.length < 6) {
      setStoreNumberError("Store number should be 6 digits");
      setStoreName("");
      setStoreData({});
    } else if (state.storeNumber?.length && state.storeNumber?.length >= 6) {
      setStoreNumberError("");
      setStoreNameError("");
    }
    if (isDefaultDataSet && isOnChangeUsed) {
      numberFieldChangeCallback(state.storeNumber, storeData);
    }
  }, [state.storeNumber]);

  useEffect(() => {
    if (state.storeNumber?.length === 0) {
      setStoreNumberError("Please enter Store Number");
    }
    if (storeName?.length === 0) {
      setStoreNameError("Please enter Store Name");
    } else {
      if (storeNameError?.length && storeNameError !== "Please enter Store Name") {
        debouncedFetchStoreName(storeName);
        setStoreName(storeName);
      }
    }
    if (isDefaultDataSet && isOnChangeUsed) {
      nameFieldChangeCallback(storeName, isValid(), storeData);
    }
  }, [storeName]);

  useEffect(() => {
    onStoreLookupValidation(!storeNumberError?.length && !storeNameError?.length);
  }, [storeNumberError, storeNameError]);

  const handleLookupModal = (isShow) => {
    setIsShowModal(isShow);
  };

  const actionsFormatter = (row) => {
    return (
      <Button variant="outline-primary" className="border-0 p-0" onClick={() => handleLookupResultSelection(row)}>
        Select
      </Button>
    );
  };

  const debouncedFetchStoreName = useCallback(
    debounce((newValue) => {
      if (newValue.length > 1) {
        fetchStoreInfo({ storeName: newValue })
          .then((res) => setNameSuggestions(res.data))
          .catch((err) => console.error(err));
      }
    }, debounceValue),
    [],
  );

  const handleFieldsChange = (e, a) => {
    setIsOnChangeUsed(true);
    const { value, id } = e.target;
    if (id === "storeNumber") {
      if (value === "" || /^[0-9\b]+$/.test(value)) {
        setState({ ...state, [id]: value });
        if (value.length === 6) {
          dispatch(showSpinner());
          fetchStoreInfo({ storeNumber: value })
            .then((res) => {
              if (res.data?.length) {
                setStoreNumberError("");
                setStoreNameError("");
                setState({ ...state, ...res.data[0] });
                setStoreName(res.data[0].storeName);
                setStoreData(res.data[0]);
              } else {
                setStoreNumberError("Store number not valid");
                setState({ ...state, [id]: value });
                setStoreName("");
                setStoreData({});
              }
              dispatch(hideSpinner());
            })
            .catch((err) => {
              console.error(err);
              dispatch(hideSpinner());
            });
        }
      }
    } else if (id === "lookupStoreState") {
      setState({ ...state, [id]: a.id });
    } else {
      setState({ ...state, [id]: value });
    }
  };

  const handleLookup = () => {
    dispatch(showSpinner());
    setState({ ...state, isShowLookupTable: false });
    fetchStoreInfo({
      storeNumber: state.lookupStoreNumber,
      storeName: state.lookupStoreName,
      state: state.lookupStoreState,
      address: state.lookupStoreAddress,
      city: state.lookupStoreCity,
      zip: state.lookupStoreZip,
    })
      .then((res) => {
        setState({ ...state, lookupResults: res.data, isShowLookupTable: true });
        dispatch(hideSpinner());
      })
      .catch((err) => {
        dispatch(hideSpinner());
        console.error(err);
      });
  };

  const handleStoreNameChange = (e) => {
    setIsNameSelected(false);
    if (e.target.value.length) {
      setStoreNameError("Select a valid name");
      setState({ ...state, storeNumber: "" });
      setStoreName(e.target.value);
      setStoreData({});
    } else {
      setStoreName(e.target.value);
    }
  };

  const handleStoreNameSelection = (eleId, eleValue, metaData) => {
    setIsOnChangeUsed(true);
    const selectedData = metaData ? JSON.parse(metaData) : {};
    setState({ ...state, ...selectedData });
    setStoreName(selectedData.storeName);
    setStoreData(selectedData);
    setStoreNameError("");
    setStoreNumberError("");
    setNameSuggestions([]);
    setIsNameSelected(true);
  };

  const handleLookupResultSelection = (row) => {
    setStoreNumberError("");
    setStoreNameError("");
    setState({ ...state, ...row });
    setStoreName(row.storeName);
    setStoreData(row);
    setIsShowModal(false);
  };

  const isValid = () => {
    return !storeNumberError.length && !storeNameError.length;
  };

  const renderDesktopStoreLookupRows = (currentData) => {
    return currentData.map((row, idx) => {
      const { id } = row;
      return (
        <tr key={id ? id : idx} className={"table-root__body__tr"}>
          {storeLookupHeaders.map((col, index) => {
            return (
              <>
                {col.name !== "Actions" && (
                  <td className={tdClass} key={index}>
                    <span>{displayDesktopRowItem(row, col)}</span>
                  </td>
                )}
                {col.name === "Actions" && (
                  <td key={index}>
                    <span>{actionsFormatter(row)}</span>
                  </td>
                )}
              </>
            );
          })}
        </tr>
      );
    });
  };
  return (
    <>
      {!createQuoteFlag && (
        <div className="col-12 form-group account-info__container">
          {!isEdit && <h4 className="label_class account-info__header px-0">Store Information</h4>}
          <p className="account-info__instruction px-0">
            Use the store lookup button to get the required information from the database
          </p>
          <Button variant="outline-primary" className="mb-4" onClick={() => handleLookupModal(true)}>
            Store Lookup
          </Button>
        </div>
      )}
      <form autoComplete="off">
        <div className="row">
          <div
            className={
              numberFieldWrapperClass
                ? numberFieldWrapperClass
                : "col-12 col-sm-12 col-md-5 search-form-container__fields__form-group"
            }
          >
            <InputField
              type="text"
              label={"Store Number"}
              id="storeNumber"
              placeholder="Enter a store number"
              fieldClasses="form-control search-form-container__fields__form-group__item"
              value={state.storeNumber}
              onChange={(e) => handleFieldsChange(e)}
              disabled={false}
              isRequired={requiredFields ? true : false}
              errorMsg={storeNumberError}
              maxLength={6}
            />
          </div>
          <div
            className={
              nameFieldWrapperClass
                ? nameFieldWrapperClass
                : "col-12 col-sm-12 col-md-5 col-lg-5 search-form-container__fields__form-group"
            }
          >
            <AutoComplete
              data={isNameSelected ? [] : nameSuggestions.map((item) => `${item.storeName}-${item.storeNumber}`)}
              id="storeName"
              label={"Store Name"}
              fieldClasses="form-control search-form-container__fields__form-group__item"
              placeholder="Enter a store name"
              onChange={handleStoreNameChange}
              debounceTime={2000}
              onSelection={handleStoreNameSelection}
              value={storeName}
              metaData={nameSuggestions}
              errorMsg={storeNameError}
              requiredField={requiredFields ? true : false}
            />
          </div>
        </div>
      </form>
      {isShowModal && (
        <Modal
          title="Store Lookup"
          handleSave={() => {}}
          handleCancel={() => handleLookupModal(false)}
          size={mobileView ? "sm" : "lg"}
          sectionName={""}
          bodyClass={"account-lookup-search-form"}
        >
          <>
            <div className="container-fluid search-form-container border-0">
              <div className="search-form-container__fields">
                <div className="d-flex search-form-container__fields__msg">
                  <div className="search-form-container__fields__msg--icon">
                    <img src={warning} alt={"warning-icon"} />
                  </div>
                  <div className="search-form-container__fields__msg--text">At least one input is required</div>
                </div>
                <form className="search-form-container__fields__form">
                  <div className="row">
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <InputField
                        type="text"
                        label={"Store Number"}
                        id="lookupStoreNumber"
                        placeholder="Enter a store number"
                        fieldClasses="form-control search-form-container__fields__form-group__item"
                        value={state.lookupStoreNumber}
                        onChange={(e) => handleFieldsChange(e)}
                        disabled={false}
                        isRequired={false}
                      />
                    </div>
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <InputField
                        type="text"
                        label={"Store Name"}
                        id="lookupStoreName"
                        placeholder="Enter a store name"
                        fieldClasses="form-control search-form-container__fields__form-group__item"
                        value={state.lookupStoreName}
                        onChange={(e) => handleFieldsChange(e)}
                        disabled={false}
                        isRequired={false}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <InputField
                        type="text"
                        label={"Store Address"}
                        id="lookupStoreAddress"
                        placeholder={"Enter a store address"}
                        fieldClasses="form-control search-form-container__fields__form-group__item"
                        value={state.lookupStoreAddress}
                        onChange={(e) => handleFieldsChange(e)}
                        disabled={false}
                        isRequired={false}
                      />
                    </div>
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <SelectInput
                        options={statesList}
                        label={"State"}
                        defaultValue={"Select a state"}
                        value={state.lookupStoreState}
                        disabled={false}
                        id="lookupStoreState"
                        handleSelectInput={(e, value) => {
                          handleFieldsChange(e, value);
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <InputField
                        type="text"
                        label={"Store City"}
                        id="lookupStoreCity"
                        placeholder={"Enter a store city"}
                        fieldClasses="form-control search-form-container__fields__form-group__item"
                        value={state.lookupStoreCity}
                        onChange={(e) => handleFieldsChange(e)}
                        disabled={false}
                        isRequired={false}
                      />
                    </div>
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                      <InputField
                        type="text"
                        label={"Store Zip"}
                        id="lookupStoreZip"
                        placeholder={"Enter a store Zip"}
                        fieldClasses="form-control search-form-container__fields__form-group__item"
                        value={state.lookupStoreZip}
                        onChange={(e) => handleFieldsChange(e)}
                        disabled={false}
                        isRequired={false}
                      />
                    </div>
                  </div>
                </form>
                <div className="d-flex search-form-container__fields__btn">
                  <Button
                    variant="outline-primary"
                    onClick={handleLookup}
                    disabled={
                      !state.lookupStoreState &&
                      !state.lookupStoreAddress &&
                      !state.lookupStoreCity &&
                      !state.lookupStoreNumber &&
                      !state.lookupStoreZip &&
                      !state.lookupStoreName
                    }
                  >
                    Search
                  </Button>
                </div>
              </div>
            </div>
            {state.isShowLookupTable && (
              <DataTable
                tableHeader={storeLookupHeaders}
                tableBody={state.lookupResults}
                parentComponent={"storeLookup"}
                countTitle="Search Results"
                renderDesktopRows={renderDesktopStoreLookupRows}
                displaySearchFilter={false}
                displaySectionTwo={false}
                isSelectAllChecked={false}
                actionsFormatter={actionsFormatter}
              />
            )}
          </>
        </Modal>
      )}
    </>
  );
};

export default StoreLookup;
